import React, { useEffect, useMemo, useRef, useState } from 'react';
import { HDate } from '@hebcal/core';
import { BOAPI } from "../service/UsersService";

const pad = (n) => String(n).padStart(2, '0');
const toDateKey = (date) => `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}`;
const todayStr = () => toDateKey(new Date());

// ── תצוגת "לוח שנה" (כמו בטאב הסטוריז) — אותו דפוס בדיוק כמו StoriesTab.jsx/HakdashotTab.jsx ──
const getHebrewDate = (date) => {
  if (!date || isNaN(date.getTime())) return '';
  return new HDate(date).renderGematriya().replace(/[֑-ׇ]/g, '');
};

const isSameDay = (a, b) => toDateKey(a) === toDateKey(b);

const MONTH_NAMES = ['ינואר', 'פברואר', 'מרץ', 'אפריל', 'מאי', 'יוני', 'יולי', 'אוגוסט', 'ספטמבר', 'אוקטובר', 'נובמבר', 'דצמבר'];
const WEEKDAY_LETTERS = ['א', 'ב', 'ג', 'ד', 'ה', 'ו', 'ש'];

// בונה את משבצות הלוח לחודש הנתון — כולל ימים "עודפים" מהחודש הקודם/הבא כדי למלא
// שבועות מלאים (6 שורות קבועות, כדי שהלוח לא "יקפוץ" בגובה בין חודשים).
const buildMonthGrid = (monthAnchor) => {
  const year = monthAnchor.getFullYear();
  const month = monthAnchor.getMonth();
  const firstOfMonth = new Date(year, month, 1);
  const startOffset = firstOfMonth.getDay(); // 0=ראשון
  const gridStart = new Date(year, month, 1 - startOffset);

  return Array.from({ length: 42 }, (_, i) => {
    const d = new Date(gridStart);
    d.setDate(gridStart.getDate() + i);
    return d;
  });
};

const fileToBase64 = (file) => new Promise((resolve, reject) => {
  const reader = new FileReader();
  reader.onload = () => resolve(reader.result);
  reader.onerror = reject;
  reader.readAsDataURL(file);
});

const emptyAd = (date) => ({
  adDate: date || todayStr(),
  sortIndex: '',
  imageUrl: '',
  description: '',
  buttonText: '',
  buttonUrl: '',
  durationSec: 6,
});

const formatDay = (ymd) => {
  const [y, m, d] = ymd.split('-').map(Number);
  const dt = new Date(y, m - 1, d);
  const dayName = dt.toLocaleDateString('he-IL', { weekday: 'long' });
  return `${dayName}, ${pad(d)}/${pad(m)}/${y}`;
};

const validate = (data) => {
  if (!data.adDate) return 'נא לבחור תאריך';
  if (!data.imageUrl?.trim()) return 'נא להוסיף תמונה';
  const dur = Number(data.durationSec);
  if (!Number.isFinite(dur) || dur < 2 || dur > 60) return 'משך התצוגה חייב להיות בין 2 ל-60 שניות';
  if (data.buttonUrl?.trim() && !/^(https?:\/\/|mailto:|tel:)/i.test(data.buttonUrl.trim())) return 'קישור הכפתור חייב להתחיל ב-https:// (או mailto: / tel:)';
  return null;
};

const toPayload = (data) => ({
  ...data,
  sortIndex: data.sortIndex === '' || data.sortIndex == null ? undefined : Number(data.sortIndex),
  imageUrl: data.imageUrl.trim(),
  description: (data.description || '').trim(),
  buttonText: (data.buttonText || '').trim(),
  buttonUrl: (data.buttonUrl || '').trim(),
  durationSec: Number(data.durationSec),
});

// שדות הטופס המשותפים להוספה ולעריכה
const AdFields = ({ data, onChange }) => {
  const fileRef = useRef(null);
  const [uploading, setUploading] = useState(false);

  const handleFile = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = '';
    if (!file) return;
    setUploading(true);
    try {
      const base64 = await fileToBase64(file);
      const ext = file.name.includes('.') ? file.name.split('.').pop() : '';
      const resp = await BOAPI.uploadAdImage(base64, ext);
      if (resp?.code === 0 && resp.url) onChange('imageUrl', resp.url);
      else window.alert(resp?.msg || 'העלאת התמונה נכשלה');
    } catch {
      window.alert('העלאת התמונה נכשלה');
    } finally {
      setUploading(false);
    }
  };

  return (
    <>
      <div style={s.formRow}>
        <div style={s.formGroup}>
          <label style={s.label}>תאריך (היום שבו תוצג) *</label>
          <input type="date" value={data.adDate} onChange={(e) => onChange('adDate', e.target.value)} style={s.input} />
        </div>
        <div style={s.formGroup}>
          <label style={s.label}>אינדקס (סדר באותו יום)</label>
          <input
            type="number"
            min="1"
            value={data.sortIndex}
            onChange={(e) => onChange('sortIndex', e.target.value)}
            style={s.input}
            placeholder="ריק = אחרון ביום"
          />
        </div>
        <div style={s.formGroup}>
          <label style={s.label}>כמה שניות מוצגת *</label>
          <input
            type="number"
            min="2"
            max="60"
            value={data.durationSec}
            onChange={(e) => onChange('durationSec', e.target.value)}
            style={s.input}
          />
        </div>
      </div>

      <div style={s.formGroup}>
        <label style={s.label}>תמונה *</label>
        <div style={s.uploadRow}>
          <input
            type="text"
            value={data.imageUrl}
            onChange={(e) => onChange('imageUrl', e.target.value)}
            style={{ ...s.input, flex: 1, minWidth: 0, direction: 'ltr' }}
            placeholder="קישור לתמונה, או בחירת קובץ מהמכשיר"
          />
          <button type="button" onClick={() => fileRef.current?.click()} style={s.uploadButton} disabled={uploading}>
            {uploading ? '⏳ מעלה...' : '📁 בחר תמונה'}
          </button>
          <input ref={fileRef} type="file" accept="image/*" onChange={handleFile} style={{ display: 'none' }} />
        </div>
        {data.imageUrl && <img src={data.imageUrl} alt="תצוגה מקדימה" style={s.preview} />}
      </div>

      <div style={s.formGroup}>
        <label style={s.label}>תיאור (אופציונלי)</label>
        <textarea
          value={data.description}
          onChange={(e) => onChange('description', e.target.value)}
          style={s.textarea}
          rows={3}
          maxLength={1000}
          placeholder="טקסט שיוצג מתחת לתמונה"
        />
      </div>

      <div style={s.formRow}>
        <div style={s.formGroup}>
          <label style={s.label}>טקסט הכפתור (אופציונלי)</label>
          <input
            type="text"
            value={data.buttonText}
            onChange={(e) => onChange('buttonText', e.target.value)}
            style={s.input}
            maxLength={60}
            placeholder='ריק = "לפרטים נוספים"'
          />
        </div>
        <div style={s.formGroup}>
          <label style={s.label}>קישור הכפתור (אופציונלי — בלי קישור אין כפתור)</label>
          <input
            type="text"
            value={data.buttonUrl}
            onChange={(e) => onChange('buttonUrl', e.target.value)}
            style={{ ...s.input, direction: 'ltr' }}
            maxLength={512}
            placeholder="https://..."
          />
        </div>
      </div>
    </>
  );
};

const AdsTab = () => {
  const [ads, setAds] = useState([]);
  const [fromDate, setFromDate] = useState(todayStr());
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const [showAdd, setShowAdd] = useState(false);
  const [newAd, setNewAd] = useState(emptyAd());
  const [editingId, setEditingId] = useState(null);
  const [editData, setEditData] = useState(null);

  // ── תצוגת לוח שנה (ברירת מחדל) ──
  const [viewMode, setViewMode] = useState('calendar'); // 'calendar' | 'table'
  const [monthAnchor, setMonthAnchor] = useState(() => new Date(new Date().getFullYear(), new Date().getMonth(), 1));
  const [dayDetailDate, setDayDetailDate] = useState(null); // יום שנלחץ בלוח — מציג את כל הפרסומות של אותו יום

  useEffect(() => { load(); }, [fromDate]);   // eslint-disable-line react-hooks/exhaustive-deps

  // הלוח מציג חודש שלם — מוודאים שהטווח הנטען מהשרת (fromDate, שאין לו תקרה עליונה)
  // מכסה גם חודשים שלפני fromDate הנוכחי כשמנווטים אחורה בלוח.
  useEffect(() => {
    if (viewMode !== 'calendar') return;
    const floor = toDateKey(new Date(monthAnchor.getFullYear(), monthAnchor.getMonth(), 1));
    if (!fromDate || floor < fromDate) setFromDate(floor);
  }, [viewMode, monthAnchor]); // eslint-disable-line react-hooks/exhaustive-deps

  const load = async () => {
    try {
      setLoading(true);
      setError(null);
      const result = await BOAPI.fetchAds(fromDate || undefined);
      if (Array.isArray(result) && result[0]?.[0]?.code === 0 && Array.isArray(result[1])) {
        setAds(result[1]);
      } else {
        setError(result?.[0]?.[0]?.msg || 'שגיאה בטעינת הנתונים — ודאו שהרצתם את AppAds.sql');
      }
    } catch (err) {
      setError(err.message || 'שגיאה בטעינת הנתונים');
    } finally {
      setLoading(false);
    }
  };

  const run = async (fn) => {
    try {
      const resp = await fn();
      const status = Array.isArray(resp) ? resp[0]?.[0] : null;
      if (status?.code === 0) { setError(null); return true; }
      setError(status?.msg || 'הפעולה נכשלה');
    } catch (err) {
      setError(err.message || 'הפעולה נכשלה');
    }
    return false;
  };

  const handleAdd = async () => {
    const err = validate(newAd);
    if (err) { setError(err); return; }
    if (await run(() => BOAPI.addAd(toPayload(newAd)))) {
      setShowAdd(false);
      setNewAd(emptyAd(newAd.adDate));
      // אם התאריך שנוסף לפני תחילת הטווח המוצג — מרחיבים את הטווח כדי שהפרסומת תופיע
      if (fromDate && newAd.adDate < fromDate) setFromDate(newAd.adDate); else load();
    }
  };

  const handleEdit = (ad) => {
    setEditingId(ad.id);
    setEditData({
      adDate: ad.adDate,
      sortIndex: ad.sortIndex,
      imageUrl: ad.imageUrl || '',
      description: ad.description || '',
      buttonText: ad.buttonText || '',
      buttonUrl: ad.buttonUrl || '',
      durationSec: ad.durationSec,
    });
  };

  const handleSaveEdit = async (id) => {
    const err = validate(editData);
    if (err) { setError(err); return; }
    if (await run(() => BOAPI.updateAd({ id, ...toPayload(editData), sortIndex: Number(editData.sortIndex) || 1 }))) {
      setEditingId(null);
      setEditData(null);
      load();
    }
  };

  const handleDelete = async (ad) => {
    if (!window.confirm(`למחוק את הפרסומת מ-${formatDay(ad.adDate)}?`)) return;
    if (await run(() => BOAPI.deleteAd(ad.id))) load();
  };

  // הוספת פרסומת נוספת לאותו יום — פותח את הטופס עם התאריך הזה
  const addToDay = (date) => {
    setDayDetailDate(null);
    setNewAd(emptyAd(date));
    setShowAdd(true);
  };

  const byDay = useMemo(() => {
    const map = new Map();
    ads.forEach((a) => {
      if (!map.has(a.adDate)) map.set(a.adDate, []);
      map.get(a.adDate).push(a);
    });
    return Array.from(map.entries());   // כבר ממוין לפי תאריך ואינדקס מהשרת
  }, [ads]);

  const adsByDate = useMemo(() => new Map(byDay), [byDay]);
  const monthGrid = useMemo(() => buildMonthGrid(monthAnchor), [monthAnchor]);
  const today = todayStr();
  const todayDate = new Date();

  const goPrevMonth = () => setMonthAnchor(m => new Date(m.getFullYear(), m.getMonth() - 1, 1));
  const goNextMonth = () => setMonthAnchor(m => new Date(m.getFullYear(), m.getMonth() + 1, 1));
  const goToday = () => setMonthAnchor(new Date(todayDate.getFullYear(), todayDate.getMonth(), 1));

  // "ערוך" מתוך פרטי היום — סוגר את החלון של היום ופותח את עריכת הפרסומת
  const editFromDayDetail = (ad) => {
    setDayDetailDate(null);
    handleEdit(ad);
  };

  if (loading) return <div style={s.center}>טוען...</div>;

  const monthLabel = `${MONTH_NAMES[monthAnchor.getMonth()]} ${monthAnchor.getFullYear()}`;
  const dayDetailKey = dayDetailDate ? toDateKey(dayDetailDate) : null;
  const dayDetailItems = dayDetailKey ? (adsByDate.get(dayDetailKey) || []) : [];

  return (
    <div style={s.container}>
      <div style={s.headerSection}>
        <div style={s.titleRow}>
          <h2 style={s.title}>פרסומות בכניסה לאפליקציה</h2>
          {ads.length > 0 && <span style={s.countBadge}>{ads.length} פרסומות</span>}
        </div>
        <div style={s.headerActions}>
          <div style={s.viewToggle}>
            <button
              onClick={() => setViewMode('table')}
              style={{ ...s.viewToggleBtn, ...(viewMode === 'table' ? s.viewToggleBtnActive : {}) }}
            >
              📋 טבלה
            </button>
            <button
              onClick={() => setViewMode('calendar')}
              style={{ ...s.viewToggleBtn, ...(viewMode === 'calendar' ? s.viewToggleBtnActive : {}) }}
            >
              📅 לוח שנה
            </button>
          </div>
          <button onClick={() => { setNewAd(emptyAd()); setShowAdd(true); }} style={s.addButton}>➕ פרסומת חדשה</button>
        </div>
      </div>
      <div style={s.hint}>
        הפרסומת מוצגת למשתמשים בכניסה לאפליקציה ביום שנבחר, למשך מספר שניות, ואפשר לדלג עליה. כמה פרסומות באותו יום מוצגות ברצף לפי האינדקס.
        כל פרסומת מוצגת פעם אחת ביום בכל מכשיר. שינויים חלים מיד, בלי עדכון גרסה.
      </div>

      {error && (
        <div style={s.error}>
          ❌ {error}
          <button onClick={() => setError(null)} style={s.closeErrorButton}>✕</button>
        </div>
      )}

      {viewMode === 'table' ? (
        <>
          <div style={s.filterRow}>
            <label style={s.filterLabel}>הצג מתאריך:</label>
            <input type="date" value={fromDate} onChange={(e) => setFromDate(e.target.value)} style={s.filterInput} />
            <button onClick={() => setFromDate(today)} style={s.linkButton}>מהיום</button>
            <button onClick={() => setFromDate('')} style={s.linkButton}>הכל (30 יום אחורה)</button>
          </div>

          {byDay.length === 0 ? (
            <div style={s.noData}>אין פרסומות בטווח שנבחר</div>
          ) : (
            byDay.map(([day, list]) => (
              <div key={day} style={s.dayBlock}>
                <div style={s.dayHeader}>
                  <b>{formatDay(day)}</b>
                  {day === today && <span style={s.todayBadge}>היום</span>}
                  {day < today && <span style={s.pastBadge}>עבר</span>}
                  <span style={s.dayCount}>{list.length} פרסומות</span>
                  <button onClick={() => addToDay(day)} style={s.linkButton}>➕ עוד ליום הזה</button>
                </div>

                {list.map((ad) => (
                  <div key={ad.id} style={s.adCard}>
                    <div style={s.adRow}>
                      <div style={s.indexBadge}>#{ad.sortIndex}</div>
                      <img src={ad.imageUrl} alt="" style={s.thumb} />
                      <div style={s.adInfo}>
                        <div style={s.adDesc}>{ad.description || <span style={s.muted}>ללא תיאור</span>}</div>
                        <div style={s.adMeta}>
                          ⏱ {ad.durationSec} שניות
                          {ad.buttonUrl && <> · 🔗 <span style={s.btnPill}>{ad.buttonText || 'לפרטים נוספים'}</span></>}
                        </div>
                      </div>
                      <div style={s.actionButtons}>
                        <button onClick={() => handleEdit(ad)} style={s.editButton}>✏️ ערוך</button>
                        <button onClick={() => handleDelete(ad)} style={s.cancelButton}>🗑️ מחק</button>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            ))
          )}
        </>
      ) : (
        <>
          <div style={s.monthNav}>
            <button onClick={goPrevMonth} style={s.navBtn}>חודש קודם</button>
            <span style={s.monthLabel}>{monthLabel}</span>
            <button onClick={goNextMonth} style={s.navBtn}>חודש הבא</button>
            <button onClick={goToday} style={s.todayBtn}>היום</button>
          </div>
          <div style={s.calendarWrap}>
            <div style={s.weekdayRow}>
              {WEEKDAY_LETTERS.map((w) => (
                <div key={w} style={s.weekdayCell}>{w}</div>
              ))}
            </div>
            <div style={s.grid}>
              {monthGrid.map((date) => {
                const key = toDateKey(date);
                const inMonth = date.getMonth() === monthAnchor.getMonth();
                const dayItems = adsByDate.get(key) || [];
                const dToday = isSameDay(date, todayDate);
                const visiblePills = dayItems.slice(0, 2);
                const extraCount = dayItems.length - visiblePills.length;
                return (
                  <div
                    key={key}
                    onClick={() => (dayItems.length > 0 ? setDayDetailDate(date) : addToDay(key))}
                    style={{
                      ...s.dayCell,
                      ...(inMonth ? {} : s.dayCellOutside),
                      ...(dayItems.length > 0 ? s.dayCellHasItems : {}),
                      ...(dToday ? s.dayCellToday : {}),
                    }}
                  >
                    <div style={s.dayCellTop}>
                      <span style={s.dayNumber}>{date.getDate()}</span>
                      {dayItems.length > 0 && <span style={s.itemsDot} />}
                    </div>
                    <div style={s.hebDateSmall}>{getHebrewDate(date)}</div>
                    <div style={s.pillsWrap}>
                      {visiblePills.map((ad) => (
                        <div
                          key={ad.id}
                          onClick={(e) => { e.stopPropagation(); handleEdit(ad); }}
                          style={s.pill}
                          title={ad.description || `פרסומת #${ad.sortIndex}`}
                        >
                          {ad.description?.trim() || `פרסומת #${ad.sortIndex}`}
                        </div>
                      ))}
                      {extraCount > 0 && (
                        <div
                          onClick={(e) => { e.stopPropagation(); setDayDetailDate(date); }}
                          style={s.morePill}
                        >
                          +{extraCount} נוספות
                        </div>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        </>
      )}

      {/* חלון "פרסומת חדשה" */}
      {showAdd && (
        <div style={s.modalOverlay} onClick={() => setShowAdd(false)}>
          <div style={s.modalPanel} onClick={(e) => e.stopPropagation()}>
            <div style={s.panelHeader}>
              <h3 style={s.formTitle}>פרסומת חדשה</h3>
              <button onClick={() => setShowAdd(false)} style={s.closeErrorButton}>✕</button>
            </div>
            <AdFields data={newAd} onChange={(f, v) => setNewAd((p) => ({ ...p, [f]: v }))} />
            <div style={s.formButtons}>
              <button onClick={handleAdd} style={s.saveButton}>➕ הוסף</button>
              <button onClick={() => setShowAdd(false)} style={s.cancelButton}>✕ בטל</button>
            </div>
          </div>
        </div>
      )}

      {/* חלון עריכת פרסומת */}
      {editingId != null && editData && (
        <div style={s.modalOverlay} onClick={() => { setEditingId(null); setEditData(null); }}>
          <div style={s.modalPanel} onClick={(e) => e.stopPropagation()}>
            <div style={s.panelHeader}>
              <h3 style={s.formTitle}>עריכת פרסומת</h3>
              <button onClick={() => { setEditingId(null); setEditData(null); }} style={s.closeErrorButton}>✕</button>
            </div>
            <AdFields data={editData} onChange={(f, v) => setEditData((p) => ({ ...p, [f]: v }))} />
            <div style={s.formButtons}>
              <button onClick={() => handleSaveEdit(editingId)} style={s.saveButton}>💾 שמור</button>
              <button onClick={() => { setEditingId(null); setEditData(null); }} style={s.cancelButton}>✕ בטל</button>
            </div>
          </div>
        </div>
      )}

      {/* פרטי יום — כל הפרסומות של היום שנלחץ בלוח */}
      {dayDetailDate && (
        <div style={s.modalOverlay} onClick={() => setDayDetailDate(null)}>
          <div style={s.dayDetailPanel} onClick={(e) => e.stopPropagation()}>
            <div style={s.panelHeader}>
              <h3 style={s.formTitle}>{getHebrewDate(dayDetailDate)} · {dayDetailDate.toLocaleDateString('he-IL')}</h3>
              <button onClick={() => setDayDetailDate(null)} style={s.closeErrorButton}>✕</button>
            </div>
            {dayDetailItems.length === 0 ? (
              <div style={s.noData}>אין פרסומות ביום הזה</div>
            ) : (
              dayDetailItems.map((ad) => (
                <div key={ad.id} style={s.dayDetailRow}>
                  <img src={ad.imageUrl} alt="" style={s.thumb} />
                  <div style={s.dayDetailInfo}>
                    <div style={s.dayDetailName}>{ad.description?.trim() || `פרסומת #${ad.sortIndex}`}</div>
                    <div style={s.dayDetailMeta}>
                      #{ad.sortIndex} · ⏱ {ad.durationSec} שניות
                      {ad.buttonUrl && ` · 🔗 ${ad.buttonText || 'לפרטים נוספים'}`}
                    </div>
                  </div>
                  <div style={s.actionButtons}>
                    <button onClick={() => editFromDayDetail(ad)} style={s.editButton}>✏️ ערוך</button>
                    <button onClick={() => handleDelete(ad)} style={s.cancelButton}>🗑️ מחק</button>
                  </div>
                </div>
              ))
            )}
            <button onClick={() => addToDay(dayDetailKey)} style={s.addDayButton}>➕ פרסומת חדשה ליום זה</button>
          </div>
        </div>
      )}
    </div>
  );
};

const s = {
  container: { padding: '20px', direction: 'rtl', fontFamily: 'Arial, sans-serif' },
  headerSection: { display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: '12px', marginBottom: '8px' },
  titleRow: { display: 'flex', alignItems: 'center', gap: '10px' },
  title: { margin: 0, color: '#1a1f36', fontSize: '24px', fontWeight: 'bold' },
  countBadge: { backgroundColor: '#eef1f6', color: '#555', padding: '4px 10px', borderRadius: '12px', fontSize: '12px', fontWeight: 'bold' },
  headerActions: { display: 'flex', alignItems: 'center', gap: '10px', flexWrap: 'wrap' },
  hint: { color: '#777', fontSize: '13px', marginBottom: '18px', lineHeight: 1.6 },
  addButton: { padding: '10px 20px', backgroundColor: '#4caf50', color: 'white', border: 'none', borderRadius: '4px', cursor: 'pointer', fontSize: '14px', fontWeight: 'bold' },

  // מתג תצוגה (טבלה / לוח שנה)
  viewToggle: { display: 'flex', backgroundColor: '#eef1f6', borderRadius: '8px', padding: '3px', gap: '2px' },
  viewToggleBtn: { padding: '7px 14px', border: 'none', background: 'transparent', color: '#555', borderRadius: '6px', cursor: 'pointer', fontSize: '13px', fontWeight: 'bold' },
  viewToggleBtnActive: { backgroundColor: '#fff', color: '#1f6feb', boxShadow: '0 1px 3px rgba(0,0,0,0.15)' },

  // לוח שנה — אותו דפוס כמו StoriesTab.jsx/HakdashotTab.jsx
  monthNav: { display: 'flex', alignItems: 'center', gap: '10px', marginBottom: '14px' },
  navBtn: { padding: '6px 12px', borderRadius: 8, border: '1px solid #ddd', background: '#fff', cursor: 'pointer', fontSize: 13, fontWeight: 'bold', color: '#334155' },
  monthLabel: { fontSize: 16, fontWeight: 'bold', color: '#1a1f36', minWidth: 140, textAlign: 'center' },
  todayBtn: { padding: '6px 14px', borderRadius: 8, border: '1px solid #C9A84C', background: 'rgba(201,168,76,0.1)', color: '#8a6d1f', cursor: 'pointer', fontSize: 13, fontWeight: 'bold' },

  calendarWrap: { backgroundColor: 'white', borderRadius: '8px', boxShadow: '0 2px 8px rgba(0,0,0,0.1)', padding: '12px', overflow: 'hidden' },
  weekdayRow: { display: 'grid', gridTemplateColumns: 'repeat(7, 1fr)', direction: 'rtl' },
  weekdayCell: { textAlign: 'center', padding: '8px 0', fontSize: 12, fontWeight: 'bold', color: '#94A3B8' },
  grid: { display: 'grid', gridTemplateColumns: 'repeat(7, 1fr)', gridAutoRows: '118px', gap: '4px', direction: 'rtl' },

  dayCell: {
    border: '1px solid #eee', borderRadius: '8px', padding: '6px', cursor: 'pointer',
    overflow: 'hidden', backgroundColor: '#fff', transition: 'background-color 0.15s', display: 'flex', flexDirection: 'column',
  },
  dayCellOutside: { backgroundColor: '#fafafa', opacity: 0.6 },
  dayCellHasItems: { backgroundColor: 'rgba(31,111,235,0.05)', borderColor: 'rgba(31,111,235,0.3)' },
  dayCellToday: { borderColor: '#1f6feb', borderWidth: '2px' },

  dayCellTop: { display: 'flex', justifyContent: 'space-between', alignItems: 'center' },
  dayNumber: { fontSize: 13, fontWeight: 'bold', color: '#333' },
  itemsDot: { width: 7, height: 7, borderRadius: '50%', backgroundColor: '#1f6feb' },
  hebDateSmall: { fontSize: 10, color: '#94A3B8', marginTop: 2, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' },

  pillsWrap: { display: 'flex', flexDirection: 'column', gap: '3px', marginTop: '4px', overflow: 'hidden' },
  pill: {
    fontSize: 10, padding: '2px 6px', borderRadius: '6px', whiteSpace: 'nowrap', overflow: 'hidden',
    textOverflow: 'ellipsis', cursor: 'pointer', fontWeight: 'bold', backgroundColor: 'rgba(31,111,235,0.1)', color: '#1f6feb',
  },
  morePill: { fontSize: 10, padding: '2px 6px', color: '#1f6feb', cursor: 'pointer', fontWeight: 'bold' },

  // חלונות מודאליים (הוספה/עריכה/פרטי יום)
  modalOverlay: {
    position: 'fixed', top: 0, left: 0, right: 0, bottom: 0,
    backgroundColor: 'rgba(15,23,42,0.45)', display: 'flex',
    alignItems: 'center', justifyContent: 'center', zIndex: 1000, padding: '20px',
  },
  modalPanel: {
    backgroundColor: '#fff', borderRadius: '12px', padding: '24px',
    width: '100%', maxWidth: '640px', maxHeight: '90vh', overflowY: 'auto',
    boxShadow: '0 10px 40px rgba(0,0,0,0.25)',
  },
  dayDetailPanel: {
    backgroundColor: '#fff', borderRadius: '12px', padding: '24px',
    width: '100%', maxWidth: '480px', maxHeight: '85vh', overflowY: 'auto',
    boxShadow: '0 10px 40px rgba(0,0,0,0.25)',
  },
  panelHeader: { display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '18px' },

  dayDetailRow: { display: 'flex', alignItems: 'center', gap: '10px', padding: '10px 0', borderBottom: '1px solid #f0f0f0' },
  dayDetailInfo: { flex: 1, minWidth: 0 },
  dayDetailName: { fontSize: '14px', fontWeight: 'bold', color: '#1a1f36' },
  dayDetailMeta: { fontSize: '12px', color: '#888', marginTop: '2px' },
  addDayButton: { marginTop: '16px', width: '100%', padding: '10px', backgroundColor: '#eef1f6', color: '#1f6feb', border: '1px dashed #1f6feb', borderRadius: '4px', cursor: 'pointer', fontSize: '13px', fontWeight: 'bold' },

  formCard: { backgroundColor: '#f9f9f9', padding: '20px', borderRadius: '8px', border: '1px solid #ddd', marginBottom: '20px' },
  formTitle: { marginTop: 0, marginBottom: '16px', color: '#333', fontSize: '18px' },
  formGroup: { marginBottom: '15px' },
  formRow: { display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))', gap: '15px' },
  label: { display: 'block', marginBottom: '5px', color: '#333', fontWeight: 'bold', fontSize: '14px' },
  input: { width: '100%', padding: '10px', fontSize: '14px', border: '1px solid #ddd', borderRadius: '4px', boxSizing: 'border-box', backgroundColor: 'white' },
  textarea: { width: '100%', padding: '10px', fontSize: '14px', border: '1px solid #ddd', borderRadius: '4px', boxSizing: 'border-box', fontFamily: 'Arial, sans-serif', resize: 'vertical' },
  uploadRow: { display: 'flex', gap: '8px', alignItems: 'stretch' },
  uploadButton: { flex: '0 0 auto', padding: '10px 14px', backgroundColor: '#eef1f6', color: '#333', border: '1px solid #ddd', borderRadius: '4px', cursor: 'pointer', fontSize: '13px', whiteSpace: 'nowrap' },
  preview: { marginTop: '10px', maxWidth: '260px', maxHeight: '260px', borderRadius: '8px', border: '1px solid #ddd', objectFit: 'contain', backgroundColor: '#fff' },
  formButtons: { display: 'flex', gap: '10px', justifyContent: 'flex-end', marginTop: '10px' },

  filterRow: { display: 'flex', alignItems: 'center', gap: '10px', flexWrap: 'wrap', marginBottom: '16px' },
  filterLabel: { fontSize: '14px', color: '#333', fontWeight: 'bold' },
  filterInput: { padding: '8px', fontSize: '14px', border: '1px solid #ddd', borderRadius: '4px' },
  linkButton: { backgroundColor: 'transparent', border: 'none', color: '#1f6feb', cursor: 'pointer', fontSize: '13px' },

  dayBlock: { marginBottom: '22px' },
  dayHeader: { display: 'flex', alignItems: 'center', gap: '10px', flexWrap: 'wrap', padding: '8px 0', borderBottom: '2px solid #eee', marginBottom: '10px', color: '#1a1f36' },
  todayBadge: { backgroundColor: '#e8f5e9', color: '#2e7d32', padding: '2px 10px', borderRadius: '10px', fontSize: '12px', fontWeight: 'bold' },
  pastBadge: { backgroundColor: '#eee', color: '#777', padding: '2px 10px', borderRadius: '10px', fontSize: '12px' },
  dayCount: { color: '#999', fontSize: '12px', marginRight: 'auto' },

  adCard: { backgroundColor: 'white', border: '1px solid #e5e5e5', borderRadius: '8px', padding: '12px', marginBottom: '10px' },
  adRow: { display: 'flex', alignItems: 'center', gap: '12px', flexWrap: 'wrap' },
  indexBadge: { backgroundColor: '#1a1f36', color: 'white', borderRadius: '6px', padding: '4px 10px', fontSize: '13px', fontWeight: 'bold' },
  thumb: { width: '72px', height: '72px', objectFit: 'cover', borderRadius: '8px', border: '1px solid #eee', backgroundColor: '#f3f3f3' },
  adInfo: { flex: 1, minWidth: '180px' },
  adDesc: { color: '#333', fontSize: '14px', marginBottom: '4px' },
  muted: { color: '#aaa' },
  adMeta: { color: '#777', fontSize: '12px' },
  btnPill: { backgroundColor: '#fff6da', color: '#8a6d1a', padding: '1px 8px', borderRadius: '8px' },
  actionButtons: { display: 'flex', gap: '8px' },
  editButton: { padding: '8px 14px', backgroundColor: '#1f6feb', color: 'white', border: 'none', borderRadius: '4px', cursor: 'pointer', fontSize: '12px', fontWeight: 'bold' },
  saveButton: { padding: '8px 16px', backgroundColor: '#2e7d32', color: 'white', border: 'none', borderRadius: '4px', cursor: 'pointer', fontSize: '13px', fontWeight: 'bold' },
  cancelButton: { padding: '8px 14px', backgroundColor: '#c62828', color: 'white', border: 'none', borderRadius: '4px', cursor: 'pointer', fontSize: '12px', fontWeight: 'bold' },

  center: { textAlign: 'center', padding: '40px', color: '#999' },
  error: { padding: '15px', backgroundColor: '#ffebee', color: '#c62828', borderRadius: '4px', marginBottom: '20px', fontSize: '14px', display: 'flex', justifyContent: 'space-between', alignItems: 'center' },
  closeErrorButton: { backgroundColor: 'transparent', color: '#c62828', border: 'none', cursor: 'pointer', fontSize: '18px', fontWeight: 'bold' },
  noData: { padding: '40px', textAlign: 'center', color: '#999', backgroundColor: '#f5f5f5', borderRadius: '4px' },
};

export default AdsTab;
